/**
 * Click Away Directive
 *
 * A Vue 3 directive that detects clicks outside of an element.
 * Useful for closing dropdowns, modals, etc.
 *
 * Usage:
 * <div v-click-away="closeDropdown">Content</div>
 *
 * @see https://vuejs.org/guide/reusability/custom-directives.html
 */

const clickAway = {
  /**
   * Called when the bound element is mounted
   *
   * @param {HTMLElement} el - The element the directive is bound to
   * @param {Object} binding - Object containing the value passed to the directive
   */
  mounted(el, binding) {
    // Store the click handler on the element for cleanup
    el._clickAwayHandler = (event) => {
      // Check if the click was outside the element and its children
      if (!(el === event.target || el.contains(event.target))) {
        // Call the provided method
        if (typeof binding.value === 'function') {
          binding.value(event);
        }
      }
    };

    // Add event listener with a small delay to avoid immediate triggering
    setTimeout(() => {
      document.addEventListener('click', el._clickAwayHandler);
    }, 0);
  },

  /**
   * Called when the bound element is unmounted
   *
   * @param {HTMLElement} el - The element the directive is bound to
   */
  unmounted(el) {
    // Clean up the event listener
    if (el._clickAwayHandler) {
      document.removeEventListener('click', el._clickAwayHandler);
      delete el._clickAwayHandler;
    }
  },
};

export default clickAway;
